import { createQueryKeys, type Query } from '@shared/api';

/**
 * Ключи запросов рейсов.
 *
 * Фабрикой описаны пять выборок, и четыре из них экраны прежде собирали литералами. Семейства
 * заводились раньше перевода потребителей нарочно: тогда перевод стал сверкой с готовой формой, а
 * не набором ключа заново по памяти. Формы повторяют прежние литералы сегмент в сегмент — перенос
 * не сдвинул ни одной ячейки кэша. Где форма выглядит случайной, это сказано прямо, а не
 * исправлено молча: промах мимо ячейки типами не ловится — экран просто читает пустую, и замечают
 * это глазами.
 *
 * Подсказку `GET /vehicle-routes/suggest` спрашивают **пять окон заведения рейса**, и до фабрики
 * каждое собирало ключ у себя (`['vehicle-routes', 'suggest', vehicleId, date]` в форме перевода в
 * работу, свой `suggestKey` в окне дня линейного заказа). Форма ключа совпадала случайно;
 * разойдись она на одну строку — и два окна спрашивали бы сервер об одной и той же машине дважды,
 * показывая при этом разное.
 */
export const vehicleRouteKeys = createQueryKeys('vehicle-routes', {
  /**
   * Что портал знает о машине на этот день: её рейсы, графы шапки прошлого рейса и закреплённые
   * за ней прицепы. Оба параметра необязательны: окно живёт и до выбора машины или даты — запрос
   * тогда выключен (`enabled`), но ключ всё равно собирается, и подставлять вместо `undefined`
   * пустую строку нельзя — это была бы другая ячейка кэша у окна, которое ничего не спрашивало.
   */
  suggest: (vehicleId: string | undefined, date: string | undefined) => [
    'suggest',
    vehicleId,
    date,
  ],
  /**
   * Рейс целиком по идентификатору: карточка рейса и переход на неё по ссылке из адреса (письмо,
   * закладка, строка заявки).
   *
   * Ключ один на двоих намеренно — рейс спрашивается один раз, а не дважды. Вторая ячейка стоила
   * бы не лишнего запроса, а расхождения версий: право на действие сервер считает по версии рейса,
   * и окно, читающее вторую копию, отдало бы 409 там, где человек ничего не менял.
   *
   * В эту же ячейку кладут свой ответ правка рейса и обе коррекции (`setQueryData`), поэтому
   * форма обязана совпасть не только с чтением, но и с записью: разъедься они — карточка после
   * сохранения показывала бы дописьменное состояние до первого перечитывания.
   *
   * `null` приходит, пока окно закрыто: запрос тогда выключен (`enabled`), но ключ собирается
   * всё равно, и подставлять вместо `null` пустую строку нельзя — у всех закрытых окон портала
   * оказалась бы одна общая ячейка.
   */
  detail: (routeId: string | null) => [routeId],
  /**
   * Журнал рейсов окна «Маршруты» (ADR 0120): отбор целиком, как его собрал экран, — вместе с
   * границами периода, которые окно подставляет само.
   *
   * Отбор объектом целиком, а не полями врозь: новый фильтр окна попадает в ключ сам, а
   * перечисленный руками список однажды забыли бы дополнить — и окно показало бы прежние строки
   * под новым фильтром.
   *
   * Сегмента с именем семейства у списка нет — ключ так и остаётся `['vehicle-routes', отбор]`, —
   * и от этого второй сегмент разнороден: здесь в нём объект отбора, у `detail` выше —
   * идентификатор рейса. Столкнуться они не могут (строка и объект дают разные ключи), но форма
   * случайная: сегмент `'list'` развёл бы эти две выборки по смыслу, а не по типу значения.
   * Оставлено как есть — сегмент это ячейка кэша, и такая правка делается отдельной и видимой.
   */
  list: (params: Query) => [params],
  /**
   * Куда переносить талон: грузовые рейсы недели вокруг дня источника — список выбора в окне
   * переноса задним числом.
   *
   * Отбора в ключе нет, и это не упущение: весь он выводится из рейса-источника (неделя с обеих
   * сторон от его дня), то есть идентификатор источника задаёт выборку однозначно. Приезжает она
   * той же ручкой `GET /vehicle-routes`, что и журнал (`list` выше), а ключ всё-таки свой: под
   * общим ключом окно переноса подменяло бы журналу список своим узким отбором, а журнал окну —
   * своим широким, и в выпадающем списке приёмников оказался бы рейс, которому талон не отдать.
   *
   * `undefined` — окно закрыто, запрос выключен (`enabled`); пустая строка вместо него собрала бы
   * ячейку, которую никто не наполняет.
   */
  transferCandidates: (routeId: string | undefined) => ['transfer-correction', routeId],
  /**
   * Что коррекция сделает с рейсом и что ей мешает (`GET /vehicle-routes/:id/correction`): сервер
   * считает последствия теми же правилами, которыми потом их и исполнит, — второй расчёт в портале
   * разошёлся бы с первым, и окно обещало бы не то, что произойдёт.
   *
   * Одно семейство на три обращения: окно коррекции рейса спрашивает свой рейс, окно переноса —
   * обе стороны по разу. Вопрос у них дословно один, и раздельные ключи означали бы лишние ходы на
   * сервер за уже полученным ответом. Каждая сторона переноса спрашивается отдельно, а не парой:
   * глубина коррекции считается от дня рейса, а дни у источника и приёмника разные.
   *
   * Имя выборки стоит **после** идентификатора — `['vehicle-routes', id, 'correction']`, — то есть
   * обратным порядком и к форме фабрики `[корень, семейство, …параметры]`, и к соседу
   * `transferCandidates`, живущему в том же окне. Форма случайная, но оставлена: сегмент — это
   * ячейка кэша. Следствие у неё одно и сегодня безвредное: расчёт лежит **под** ключом карточки
   * (`detail`), поэтому точечное гашение одной карточки уносит и её расчёт, — после правки рейса
   * расчёт и правда устаревает, так что совпало.
   */
  correctionPreview: (routeId: string | undefined) => [routeId, 'correction'],
});
